/**
 * BehaviorTracker - Comprehensive user behavior tracking service
 * Tracks: clicks, likes, saves, shares, views, reading patterns, searches, locations
 * Batches data and sends to backend every 30 seconds or after 10 events
 */

import axios from 'axios';
import { extractKeywords, buildBigrams } from '../../utils/keywordCleaner';

class BehaviorTracker {
  constructor({ userId, context = 'explore', language = 'TH', flushInterval = 60000 }) {
    // --- Anonymous/guest support ---
    this.anonId = BehaviorTracker.getOrCreateAnonId();
    this.userId = userId || null;
    this.effectiveUserId = userId || this.anonId; // always present
    this.context = context;
    this.language = language;

    // Behavior queues
    this.interactionQueue = [];
    this.viewQueue = [];
    this.readingQueue = [];
    this.searchQueue = [];
    this.locationQueue = [];

    // Keyword interest tracking
    this.keywordBuffer = new Map(); // Accumulates keyword weights in-session

    // Configuration (Option D: 1-minute flush interval)
    this.config = {
      batchSize: 10,
      flushInterval: flushInterval, // Now configurable, defaults to 60000ms (1 minute)
      maxQueueSize: 100,
      apiUrl: process.env.REACT_APP_API_URL
    };

    this.startAutoFlush();

    // Session continuity - reuse existing session ID if available
    const existingSessionId = localStorage.getItem('current_session_id');
    this.sessionId = existingSessionId || this.generateSessionId();
    localStorage.setItem('current_session_id', this.sessionId);

    this.sessionStart = Date.now();

    console.log('📊 [BehaviorTracker] Initialized:', {
      userId: this.userId,
      anonId: this.anonId,
      effectiveUserId: this.effectiveUserId,
      context,
      language,
      sessionId: this.sessionId
    });

    // --- Session/visibility tracking (1-min heartbeat) ---
    this.page = null;
    this.lastActiveAt = Date.now();
    this.activeMs = 0;
    this.isVisible = true;
    this.heartbeatIntervalMs = 60000; // 1 minute
    this.heartbeatTimer = null;

    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', this._onVisibilityChange);
    }
    this._startHeartbeat();
  }

  // ---------- Anonymous ID ----------
  static getOrCreateAnonId() {
    try {
      const key = 'anon_user_id';
      const existing = localStorage.getItem(key);
      if (existing) return existing;
      const id = 'anon_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(key, id);
      return id;
    } catch {
      // Fallback (non-persistent)
      return 'anon_' + Math.random().toString(36).slice(2);
    }
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // ---------- Weight Normalization ----------
  /**
   * Normalize weight to [0, 1] range
   * @param {number} w - Weight value to normalize
   * @param {number} fallback - Fallback value if weight is invalid (default: 0.5)
   * @returns {number} Normalized weight in [0, 1]
   */
  _normalizeWeight(w, fallback = 0.5) {
    const num = Number(w);
    if (!Number.isFinite(num)) return fallback;
    if (num < 0) return 0;
    if (num > 1) return 1;
    return num;
  }

  // ---------- Visibility & Heartbeat ----------
  _markActiveNow() {
    const now = Date.now();
    if (this.isVisible) {
      this.activeMs += (now - this.lastActiveAt);
    }
    this.lastActiveAt = now;
  }

  _onVisibilityChange() {
    const now = Date.now();
    if (document.visibilityState === 'hidden') {
      if (this.isVisible) {
        this.activeMs += (now - this.lastActiveAt);
      }
      this.isVisible = false;
      this.lastActiveAt = now;
    } else {
      this.isVisible = true;
      this.lastActiveAt = now;
    }
  }

  _startHeartbeat() {
    // Prevent duplicate intervals (Option D)
    if (this.heartbeatTimer) {
      console.warn('🔄 [BehaviorTracker] Heartbeat already running, skipping duplicate start');
      return;
    }
    this.heartbeatTimer = setInterval(() => {
      this.trackInteraction({
        eventId: null,
        action: 'session_heartbeat',
        context: this.context,
        metadata: { page: this.page },
        timestamp: Date.now()
      });
    }, this.heartbeatIntervalMs);
    console.log('💓 [BehaviorTracker] Heartbeat started (60s interval)');
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // ---------- Visit Frequency ----------
  static bumpLocalCounter(key) {
    try {
      const curr = parseInt(localStorage.getItem(key) || '0', 10);
      localStorage.setItem(key, String(curr + 1));
    } catch {}
  }

  trackAppVisit() {
    const dayKey = new Date().toISOString().slice(0, 10);
    BehaviorTracker.bumpLocalCounter(`visits:app:${dayKey}`);
    this.trackInteraction({
      eventId: null,
      action: 'app_visit',
      context: this.context,
      metadata: { dayKey },
      timestamp: Date.now()
    });
  }

  trackPageVisit(pageName) {
    const dayKey = new Date().toISOString().slice(0, 10);
    BehaviorTracker.bumpLocalCounter(`visits:${pageName}:${dayKey}`);
    this.trackInteraction({
      eventId: null,
      action: 'page_visit',
      context: this.context,
      metadata: { page: pageName, dayKey },
      timestamp: Date.now()
    });
  }

  // ---------- Session Lifecycle ----------
  trackSessionStart(pageName = 'unknown') {
    this.page = pageName;
    this.sessionStart = Date.now();
    this.lastActiveAt = Date.now();
    this.isVisible = true;
    this.activeMs = 0;

    // Count app & page visit at session start (robust and simple)
    this.trackAppVisit();
    this.trackPageVisit(pageName);

    this.trackInteraction({
      eventId: null,
      action: 'session_start',
      context: this.context,
      metadata: {
        page: pageName,
        sessionId: this.sessionId,
        effectiveUserId: this.effectiveUserId,
        userId: this.userId,
        anonId: this.anonId
      },
      timestamp: this.sessionStart
    });
  }

  trackSessionEnd(reason = 'unmount') {
    this._markActiveNow();
    const durationMs = Date.now() - this.sessionStart;
    const activeMs = this.activeMs;

    // Flush accumulated keywords before ending session
    if (this.keywordBuffer && this.keywordBuffer.size) {
      const keywordPayload = Array.from(this.keywordBuffer.entries())
        .map(([keyword, weight]) => ({
          keyword,
          // Ensure 0..1 range
          weight: this._normalizeWeight(weight, 0.5)
        }));

      this.trackInteraction({
        eventId: null,
        action: 'keyword_flush',
        context: this.context,
        metadata: {
          page: this.page,
          keywords: keywordPayload,
          source: 'session_flush'
        },
        timestamp: Date.now()
      });

      this.keywordBuffer.clear();
      console.log('🏷️ [BehaviorTracker] Keywords flushed:', keywordPayload.length);
    }

    this.trackInteraction({
      eventId: null,
      action: 'session_end',
      context: this.context,
      metadata: {
        page: this.page,
        sessionId: this.sessionId,
        durationMs,
        activeMs,
        reason
      },
      timestamp: Date.now()
    });
  }

  // ---------- Queue Size Management (3.4) ----------
  getTotalQueueSize() {
    return (
      this.interactionQueue.length +
      this.viewQueue.length +
      this.readingQueue.length +
      this.searchQueue.length +
      this.locationQueue.length
    );
  }

  trackInteraction({ eventId, action, context = this.context, position = null, metadata = {}, timestamp = Date.now() }) {
    // Enforce maxQueueSize (3.4)
    if (this.getTotalQueueSize() >= this.config.maxQueueSize) {
      console.warn('[BehaviorTracker] Max queue size reached, dropping interaction:', action, eventId);
      return;
    }

    const interaction = {
      userId: this.effectiveUserId, // unified id (user or anon)
      sessionId: this.sessionId,
      eventId,
      action,
      context,
      position,
      language: this.language,
      timestamp,
      metadata
    };

    this.interactionQueue.push(interaction);
    console.log('👆 [BehaviorTracker] Interaction tracked:', action, eventId);
    this.checkAndFlush();
  }

  trackEventImpression({ eventIds, context = this.context, timestamp = Date.now() }) {
    // Enforce maxQueueSize (3.4)
    if (this.getTotalQueueSize() >= this.config.maxQueueSize) {
      console.warn('[BehaviorTracker] Max queue size reached, dropping impression');
      return;
    }

    const impression = {
      userId: this.effectiveUserId,
      sessionId: this.sessionId,
      eventIds,
      context,
      language: this.language,
      timestamp
    };

    this.viewQueue.push(impression);
    console.log('👁️ [BehaviorTracker] Impression tracked:', eventIds.length, 'events');
    this.checkAndFlush();
  }

  trackEventView({ eventId, duration, context = this.context, timestamp = Date.now() }) {
    // Enforce maxQueueSize (3.4)
    if (this.getTotalQueueSize() >= this.config.maxQueueSize) {
      console.warn('[BehaviorTracker] Max queue size reached, dropping view:', eventId);
      return;
    }

    const view = {
      userId: this.effectiveUserId,
      sessionId: this.sessionId,
      eventId,
      duration,
      context,
      language: this.language,
      timestamp
    };

    this.viewQueue.push(view);
    console.log('⏱️ [BehaviorTracker] View tracked:', eventId, `${(duration/1000).toFixed(1)}s`);
    this.checkAndFlush();
  }

  trackReadingBehavior({ eventId, totalTime, scrollDepth, pausePoints = [], sectionsViewed = [], qualityMetrics = {}, context = this.context, timestamp = Date.now() }) {
    // Enforce maxQueueSize (3.4)
    if (this.getTotalQueueSize() >= this.config.maxQueueSize) {
      console.warn('[BehaviorTracker] Max queue size reached, dropping reading behavior:', eventId);
      return;
    }

    const reading = {
      userId: this.effectiveUserId,
      sessionId: this.sessionId,
      eventId,
      totalTime,
      scrollDepth,
      pausePoints,
      sectionsViewed,
      qualityMetrics,  // Added for backend DB storage
      context,
      language: this.language,
      timestamp
    };

    this.readingQueue.push(reading);
    console.log('📖 [BehaviorTracker] Reading behavior tracked:', eventId);
    this.checkAndFlush();
  }

  trackSearch({ query, filters = {}, resultsCount, selectedPosition = null, selectedEventId = null, context = this.context, timestamp = Date.now() }) {
    // Enforce maxQueueSize (3.4)
    if (this.getTotalQueueSize() >= this.config.maxQueueSize) {
      console.warn('[BehaviorTracker] Max queue size reached, dropping search:', query);
      return;
    }

    const search = {
      userId: this.effectiveUserId,
      sessionId: this.sessionId,
      query,
      filters,
      resultsCount,
      selectedPosition,
      selectedEventId,
      context,
      language: this.language,
      timestamp
    };

    this.searchQueue.push(search);
    console.log('🔍 [BehaviorTracker] Search tracked:', query || '(empty)');
    this.checkAndFlush();
  }

  trackLocation({ latitude, longitude, accuracy, context = this.context, timestamp = Date.now() }) {
    // Enforce maxQueueSize (3.4)
    if (this.getTotalQueueSize() >= this.config.maxQueueSize) {
      console.warn('[BehaviorTracker] Max queue size reached, dropping location');
      return;
    }

    const location = {
      userId: this.effectiveUserId,
      sessionId: this.sessionId,
      latitude,
      longitude,
      accuracy,
      context,
      timestamp
    };

    this.locationQueue.push(location);
    console.log('📍 [BehaviorTracker] Location tracked:', { latitude, longitude });
    this.checkAndFlush();
  }

  /**
   * Track interest keywords from raw text or tokens.
   * Accepts: string | string[]
   * Automatically cleans, tokenizes, and optionally builds bigrams.
   *
   * @param {string|string[]} keywords - Raw text or array of keywords
   * @param {Object} options - Configuration options
   * @param {string} options.source - Source of keywords (e.g., 'search', 'event_click')
   * @param {string} options.eventId - Associated event ID (if applicable)
   * @param {number} options.weight - Weight to assign to keywords (default: 1)
   * @param {string} options.lang - Language code for processing
   * @param {boolean} options.makeBigrams - Build bigrams from tokens (default: true)
   * @param {number} options.maxKeywords - Maximum keywords to track (default: 20)
   * @param {string[]|Set<string>} options.extraStop - Additional stopwords
   * @param {boolean} options.skipClean - Skip cleaning for pre-cleaned arrays (default: false)
   */
  trackKeywordInterest(keywords, {
    source = 'unknown',
    eventId = null,
    weight = 0.5,           // Changed default to 0.5 (mid-range in [0,1])
    lang = this.language,
    makeBigrams = true,
    maxKeywords = 20,
    extraStop = [],
    skipClean = false
  } = {}) {
    let tokens = [];

    if (Array.isArray(keywords)) {
      tokens = keywords.slice();
      if (!skipClean) {
        // Minimal clean pass on arrays - normalize and filter empty
        tokens = tokens
          .map(t => String(t || '').trim().toLowerCase())
          .filter(Boolean);
      }
    } else if (typeof keywords === 'string') {
      // Extract keywords from raw text using multilingual cleaner
      tokens = extractKeywords(keywords, { lang, extraStop });
    }

    if (tokens.length === 0) return;

    // Build bigrams if requested
    if (makeBigrams) {
      tokens = buildBigrams(tokens);
    }

    // Cap the count for payload sanity
    tokens = tokens.slice(0, maxKeywords);

    // Normalize weight to [0, 1] range
    const normWeight = this._normalizeWeight(weight, 0.5);

    // Buffer in session keyword map - accumulate but keep ceiling of 1.00
    if (!this.keywordBuffer) this.keywordBuffer = new Map();
    for (const kw of tokens) {
      const cur = this.keywordBuffer.get(kw) || 0;
      // Accumulate but cap at 1.0
      this.keywordBuffer.set(kw, Math.min(1, cur + normWeight));
    }

    // Emit a lightweight interaction immediately for real-time analytics stream
    this.trackInteraction({
      eventId,
      action: 'keyword_interest',
      context: this.context,
      metadata: {
        keywords: tokens,
        source,
        page: this.page,
        weight: normWeight  // Use normalized weight
      },
      timestamp: Date.now()
    });

    console.log('🏷️ [BehaviorTracker] Keywords tracked:', {
      source,
      count: tokens.length,
      sample: tokens.slice(0, 5),
      weight: normWeight
    });
  }

  checkAndFlush() {
    const totalQueueSize =
      this.interactionQueue.length +
      this.viewQueue.length +
      this.readingQueue.length +
      this.searchQueue.length +
      this.locationQueue.length;

    if (totalQueueSize >= this.config.batchSize) {
      console.log('📤 [BehaviorTracker] Queue threshold reached, flushing...');
      this.flush();
    }
  }

  async flush() {
    // NOTE: we always have an effective id (user or anon). Never skip flush.

    const hasData =
      this.interactionQueue.length > 0 ||
      this.viewQueue.length > 0 ||
      this.readingQueue.length > 0 ||
      this.searchQueue.length > 0 ||
      this.locationQueue.length > 0;

    if (!hasData) return;

    const payload = {
      userId: this.effectiveUserId,
      sessionId: this.sessionId,
      language: this.language,
      interactions: [...this.interactionQueue],
      views: [...this.viewQueue],
      readings: [...this.readingQueue],
      searches: [...this.searchQueue],
      locations: [...this.locationQueue],
      timestamp: Date.now()
    };

    // Clear queues immediately
    this.interactionQueue = [];
    this.viewQueue = [];
    this.readingQueue = [];
    this.searchQueue = [];
    this.locationQueue = [];

    try {
      const response = await axios.post(
        `${this.config.apiUrl}/behavior/track`,
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        }
      );

      console.log('✅ [BehaviorTracker] Data flushed successfully:', {
        interactions: payload.interactions.length,
        views: payload.views.length,
        readings: payload.readings.length,
        searches: payload.searches.length,
        locations: payload.locations.length
      });
    } catch (error) {
      console.error('❌ [BehaviorTracker] Flush failed:', error);
      this.storeFailedData(payload);
    }
  }

  storeFailedData(payload) {
    try {
      const failedData = JSON.parse(localStorage.getItem('behaviorTrackingFailed') || '[]');
      failedData.push({ ...payload, failedAt: Date.now() });
      const trimmed = failedData.slice(-50);
      localStorage.setItem('behaviorTrackingFailed', JSON.stringify(trimmed));
      console.log('💾 [BehaviorTracker] Failed data stored for retry');
    } catch (e) {
      console.error('❌ [BehaviorTracker] Failed to store failed data:', e);
    }
  }

  async retryFailedData() {
    try {
      const failedData = JSON.parse(localStorage.getItem('behaviorTrackingFailed') || '[]');
      if (failedData.length === 0) return;

      console.log('🔄 [BehaviorTracker] Retrying failed data:', failedData.length, 'batches');

      for (const payload of failedData) {
        try {
          await axios.post(`${this.config.apiUrl}/behavior/track`, payload, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 10000
          });
        } catch (error) {
          console.error('❌ [BehaviorTracker] Retry failed for batch:', error);
          continue;
        }
      }

      localStorage.removeItem('behaviorTrackingFailed');
      console.log('✅ [BehaviorTracker] Failed data retry complete');
    } catch (error) {
      console.error('❌ [BehaviorTracker] Retry process failed:', error);
    }
  }

  startAutoFlush() {
    this.flushTimer = setInterval(() => {
      console.log('⏰ [BehaviorTracker] Auto-flush triggered');
      this.flush();
      this.retryFailedData();
    }, this.config.flushInterval);
  }

  stopAutoFlush() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  cleanup() {
    console.log('🧹 [BehaviorTracker] Cleanup started');

    // Stop heartbeat first (Option D)
    this._stopHeartbeat();

    // Stop auto-flush timer
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Flush any remaining data
    try {
      this.trackSessionEnd('cleanup');
      this.flush();
    } catch (err) {
      console.warn('🧹 [BehaviorTracker] Cleanup flush failed:', err);
    }

    // Clear all queues (Option D)
    this.interactionQueue = [];
    this.viewQueue = [];
    this.readingQueue = [];
    this.searchQueue = [];
    this.locationQueue = [];

    // Remove visibility listener
    if (typeof document !== 'undefined' && document.removeEventListener) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }

    console.log('✅ [BehaviorTracker] Cleanup completed');
  }
}

export default BehaviorTracker;
